import { useContext, useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from '@mui/material'
import { Add, CheckCircleOutline, InfoOutlined, SwapHoriz } from '@mui/icons-material'
import { IconName } from '../../../../shared/interfaces/icon-picker.model'
import { IconPicker } from '../../display-elements-components/icon-picker'
import { IEmissionCalculation } from '../../../../shared/services/emission-calculation/emission-calculation.model'
import { IConversion } from '../../../../shared/services/conversion/conversion.model'
import { IEmissionDetail } from '../../../../shared/services/emission/emission.model'
import { TableActionButtons } from '../../../presentation-components/miscellaneous-components/table-action-buttons'
import { TooltipHover } from '../../miscellaneous-components/tooltip-hover'
import { AddManualSpendBasedDialog } from '../../../dialogs/emissions/add-manual-spend-based-dialog'
import { EditEmissionCalculationDialog } from '../../../dialogs/emissions/edit-emission-calculation-dialog'
import { EmissionsAddManual } from '../../../presentation-components/emissons-components/overview/emissions-add-manual'
import { EmissionsGoodCatalogue } from '../../../presentation-components/emissons-components/overview/emissions-good-catalogue'
import { EmissionsInputDirect } from './emissions-input/emissions-input-direct'
import { GOODS_GROUP_IDS } from '../../../../shared/constants/group-ids'
import { DISTRIBUTION_LOSS_CATEGORY_IDS } from '../../../../shared/constants/category-ids'
import { formatGroupLabel, sortGroupsByGhgCategory } from '../../../../shared/constants/ghg-category'
import GroupService from '../../../../shared/services/group/group.service'
import { IGroup, IGroupDetail } from '../../../../shared/services/group/group.model'
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service'
import { ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model'
import ConversionSourceService from '../../../../shared/services/conversion-source/conversion-source.service'
import { IConversionSource } from '../../../../shared/services/conversion-source/conversion-source.model'
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications'
import { getPreferredSubsidiary } from '../../../../shared/utils/subsidiary-utils'
import { UserDetailsContext } from '../../../../context/user-details/user-details'
import EmissionCalculationService, { ISupersedeDecision } from '../../../../shared/services/emission-calculation/emission-calculation.service'
import CategoryService from '../../../../shared/services/category/category.service'
import { ICategory } from '../../../../shared/services/category/category.model'
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked'
import {
  formatCalculationPeriodLabel,
  formatCalculationQualityLabel,
  formatContributionToSelectedLabel,
  formatKgCo2e,
  formatPercentVsPrimary,
} from '../../../../shared/utils/emission-summary-utils'
import { getCategoryDisplayName } from '../../../../shared/utils/category-utils'

interface EmissionDetailTableProps {
  calculations: IEmissionCalculation[]
  onView: (ids: string[]) => void
  onDelete?: (ids: string[], isLastRecord: boolean) => void
  onAddSuccess: () => void
  emission: IEmissionDetail
}

interface CalculationGroup {
  key: string
  typeLabel: string
  scope: string
  calculations: IEmissionCalculation[]
}

const groupService = GroupService.getInstance()
const subsidiaryService = SubsidiaryService.getInstance()

// MUI MenuItem defaults to nowrap, which runs the longer captions off the edge of the
// menu paper. Let the description wrap under its title instead.
const addCalculationMenuItemSx = { py: 1.25, alignItems: 'flex-start', whiteSpace: 'normal' } as const

/** API / Sequelize may send 0/1 or strings; avoids mis-routing (e.g. unselected calc treated as selected). */
function coerceBool(value: unknown): boolean {
  if (value === true || value === 1) return true
  if (value === false || value === 0) return false
  if (value === null || value === undefined) return false
  if (typeof value === 'string') {
    const s = value.toLowerCase().trim()
    if (s === 'true' || s === '1') return true
    if (s === 'false' || s === '0' || s === '') return false
  }
  return Boolean(value)
}

/**
 * Explains the Additional emissions section. Kept general rather than electricity-only,
 * because this section also carries the upstream rows for fuels, vehicles and heat. The
 * two-upstream-rows case on an electricity record is the one people ask about, so it is
 * called out explicitly.
 */
const ADDITIONAL_EMISSIONS_TOOLTIP = [
  'These are emissions caused by this activity that happen somewhere other than the point of use.',
  'All of them are Scope 3, so the headline figure on this record does not change.',
  '',
  'Upstream emissions (WTT) is well-to-tank: extracting, refining and transporting the fuel before',
  'it is burned. An electricity record can show two, because the fuel supply chain is counted',
  'separately for the electricity that reached you and for the part lost on the way.',
  '',
  'Transmission & distribution losses apply to grid electricity (including electric vehicle charging)',
  'and district heat. Around 9% of what',
  'is generated is lost as heat in cables and transformers, so more had to be generated than you',
  'received. This row is the emissions of generating the part that never arrived.',
].join(' ').replace(/\s+/g, ' ').trim()

/**
 * Whether an upstream row is a distribution-loss row: its category is one of the DEFRA loss
 * factor categories (grid and district heat, and EV charging). Those rows share the isWtt flag
 * with well-to-tank rows, because every read path already treats them the same way, but they are
 * not well-to-tank emissions and must not be labelled as such: they are the emissions of the
 * energy lost in transit, and they follow the losses setting, not the well-to-tank one.
 */
function isDistributionLoss(calc: IEmissionCalculation): boolean {
  return !!calc.category?.id && DISTRIBUTION_LOSS_CATEGORY_IDS.includes(calc.category.id)
}

function formatMethodLabel(calc: IEmissionCalculation): string {
  const wtt = coerceBool(calc.isWtt)
  if (calc.type === 'SPEND') return 'Spend-based'
  if (calc.type === 'PRODUCT') return 'Product-based'
  if (calc.type === 'ACTIVITY') {
    if (isDistributionLoss(calc)) return 'Activity-based (T&D losses)'
    if (wtt) return 'Activity-based (WTT)'
    if (calc.isMarketBased) return 'Activity-based (market-based)'
    if (calc.scope === '2' && !calc.isMarketBased && !wtt) return 'Activity-based (location-based)'
    return 'Activity-based'
  }
  if (calc.type === 'DIRECT') return 'Direct'
  if (calc.type === 'MEASUREMENT') return 'Measurement'
  return calc.type
}

function sumGroupKg(group: CalculationGroup): number {
  return group.calculations.reduce((s, c) => s + (c.emissionAmount ?? 0), 0)
}

/** Human-readable description of what a calculation refers to, e.g. "Business travel › Land › Motorbike". */
function formatActivityReference(calc: IEmissionCalculation): string {
  const join = (parts: Array<string | null | undefined>) =>
    parts
      .filter((p): p is string => typeof p === 'string' && p.trim().length > 0)
      .map((p) => p.trim())
      .join(' › ')

  if (calc.goodCategory) {
    const reference = join([
      calc.goodCategory.category,
      calc.goodCategory.subCategoryOne,
      calc.goodCategory.subCategoryTwo,
    ])
    if (reference) return reference
  }

  const conversionReference = (c: IConversion | null | undefined) =>
    c
      ? join([
        c.detailLevelOne,
        c.detailLevelTwoFriendlyName || c.detailLevelTwo,
        c.detailLevelThreeFriendlyName || c.detailLevelThree,
        c.detailLevelFourFriendlyName || c.detailLevelFour,
        c.detailLevelFiveFriendlyName || c.detailLevelFive,
      ])
      : ''

  // Activity-based: prefer the standard/location-based conversion, then the market-based one
  // (market-based electricity stores its hierarchy on marketBasedConversion, not conversion).
  const activityReference = conversionReference(calc.conversion) || conversionReference(calc.marketBasedConversion)
  if (activityReference) return activityReference

  if (calc.spendBasedConversion?.activity) {
    return calc.spendBasedConversion.activity.trim()
  }

  if (calc.category) {
    return getCategoryDisplayName(calc.category)
  }

  return ''
}

/** Right-hand status: "Included in 2026 reports" using report period calendar year. */
function formatIncludedInReportLabel(emission: IEmissionDetail): string {
  const raw = emission.reportPeriod?.calendarYear?.trim()
  if (raw) {
    const m = raw.match(/\d{4}/)
    const year = m ? m[0] : raw
    return `Included in ${year} reports`
  }
  return 'Included in reports'
}

/** Scope 2 activity (non-WTT) with both market- and location-based rows: partner row is not listed separately. */
function hasScope2MarketLocationPair(calculations: IEmissionCalculation[]): boolean {
  const s2 = calculations.filter(
    (c) => c.scope === '2' && c.type === 'ACTIVITY' && !coerceBool(c.isWtt),
  )
  return s2.some((c) => c.isMarketBased) && s2.some((c) => !c.isMarketBased)
}

/**
 * A record whose period crosses 1 January holds one location/market pair per calendar year, so a
 * row's partner is the opposite basis in the SAME year. This is the pairing the reports use
 * (reportSelection.helper on the backend matches type + calendar year, null-safe for legacy rows).
 */
function sameCalendarYear(a: IEmissionCalculation, b: IEmissionCalculation): boolean {
  return String(a.calendarYear ?? '') === String(b.calendarYear ?? '')
}

// Exported for tests.
export function getScope2OtherBasisCalculation(
  calculations: IEmissionCalculation[],
  primaryCalc: IEmissionCalculation,
): IEmissionCalculation | undefined {
  if (!hasScope2MarketLocationPair(calculations)) return undefined
  if (primaryCalc.scope !== '2' || primaryCalc.type !== 'ACTIVITY' || coerceBool(primaryCalc.isWtt)) return undefined
  const wantOppositeMarket = !primaryCalc.isMarketBased
  return calculations.find(
    (c) =>
      c.scope === '2' &&
      c.type === 'ACTIVITY' &&
      !coerceBool(c.isWtt) &&
      c.isMarketBased === wantOppositeMarket &&
      sameCalendarYear(c, primaryCalc),
  )
}

// Exported for tests.
export function partitionCalculationGroups(calculations: IEmissionCalculation[]): {
  measurementCalcs: IEmissionCalculation[]
  primaryGroups: CalculationGroup[]
  additionalGroups: CalculationGroup[]
  alternativeGroups: CalculationGroup[]
  scope2MarketLocationPair: boolean
} {
  const measurementCalcs = calculations.filter((c) => c.type === 'MEASUREMENT')
  /**
   * MEASUREMENT calcs are unit-conversion steps (e.g. ft²→m²→kWh) that carry no CO2e of their own.
   * They are surfaced as part of the primary calculation's drill-down (see matchingMeasurementIds /
   * viewCalculationIds), so they must NOT be grouped as primary/additional/alternative cards. Left in,
   * an unselected 0-CO2e conversion step renders as an "Alternative calculation" reading "-100% vs primary".
   */
  const tableCalcs = calculations.filter((c) => c.type !== 'MEASUREMENT')
  const scope2MarketLocationPair = hasScope2MarketLocationPair(calculations)

  const groupedPrimary: Record<string, CalculationGroup> = {}
  const groupedAdditional: Record<string, CalculationGroup> = {}
  const groupedAlternative: Record<string, CalculationGroup> = {}

  tableCalcs.forEach((calc) => {
    const selected = coerceBool(calc.isSelected)
    const wtt = coerceBool(calc.isWtt)

    if (
      scope2MarketLocationPair &&
      !selected &&
      calc.scope === '2' &&
      calc.type === 'ACTIVITY' &&
      !wtt
    ) {
      const hasSelectedPartner = calculations.some(
        (c) =>
          c.scope === '2' &&
          c.type === 'ACTIVITY' &&
          !coerceBool(c.isWtt) &&
          c.isMarketBased !== calc.isMarketBased &&
          sameCalendarYear(c, calc) &&
          coerceBool(c.isSelected),
      )
      /** Unselected market/location basis is shown only as secondary line under the selected row. */
      if (hasSelectedPartner) return
    }

    const typeLabel =
      calc.type === 'ACTIVITY' && calc.isMarketBased
        ? 'ACTIVITY - Market-based'
        : calc.type === 'ACTIVITY' && isDistributionLoss(calc)
          ? 'ACTIVITY - T&D losses'
          : calc.type === 'ACTIVITY' && wtt
            ? 'ACTIVITY - WTT'
          : calc.type === 'ACTIVITY' && !calc.isMarketBased && !wtt && calc.scope === '2'
            ? 'ACTIVITY - Location-based'
            : calc.type

    // One box per calculation: key on the calculation id so like calculations are
    // no longer merged into a single summed line and can be selected/deleted individually.
    const key = calc.id

    const target = wtt ? groupedAdditional : selected ? groupedPrimary : groupedAlternative

    if (!target[key]) {
      target[key] = { key, typeLabel, scope: calc.scope, calculations: [] }
    }

    target[key].calculations.push(calc)
  })

  return {
    measurementCalcs,
    primaryGroups: Object.values(groupedPrimary),
    additionalGroups: Object.values(groupedAdditional),
    alternativeGroups: Object.values(groupedAlternative),
    scope2MarketLocationPair,
  }
}

export const EmissionDetailTable = ({ calculations, onView, onDelete, onAddSuccess, emission }: EmissionDetailTableProps) => {
  const reportPeriodContext = useContext(ReportPeriodContext)
  const userNotificationsContext = useContext(UserNotificationsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const userDetails = userDetailsContext.userDetails
  const isReportPeriodLocked = useIsReportPeriodLocked()
  const canManageCalculations = !!userDetails?.roles?.some((role) => role === 'admin' || role === 'analyst')
  const canMutateCalculations = canManageCalculations && !isReportPeriodLocked
  const emissionCalculationService = new EmissionCalculationService()
  const conversionSourceService = new ConversionSourceService()
  const categoryService = new CategoryService()

  const [openAddManualDialog, setOpenAddManualDialog] = useState(false)
  const [openEditCalculationDialog, setOpenEditCalculationDialog] = useState(false)
  const [selectedEditCalculation, setSelectedEditCalculation] = useState<IEmissionCalculation | null>(null)
  const [selectedEditCalculationIds, setSelectedEditCalculationIds] = useState<string[]>([])
  const [showAddGoodsCatalogueView, setShowAddGoodsCatalogueView] = useState(false)
  const [showAddActivityView, setShowAddActivityView] = useState(false)
  const [showAddDirectView, setShowAddDirectView] = useState(false)
  const [directGroupId, setDirectGroupId] = useState<string>('')
  const [directCategoryId, setDirectCategoryId] = useState<string>('')
  const [categories, setCategories] = useState<ICategory[]>([])
  // Resolves when the user has chosen replace-or-count-both. Held while the choice dialog
  // is open so every add flow can await the same decision before it calls the service.
  const [supersedeRequest, setSupersedeRequest] = useState<{
    resolve: (decision: ISupersedeDecision) => void
    reject: () => void
  } | null>(null)
  const [addCalculationMenuAnchor, setAddCalculationMenuAnchor] = useState<null | HTMLElement>(null)
  const addCalculationMenuOpen = Boolean(addCalculationMenuAnchor)

  const [groups, setGroups] = useState<IGroup[]>([])
  const [selectedGroupId, setSelectedGroupId] = useState<string>('')
  const [group, setGroup] = useState<IGroupDetail>()
  const [conversionSources, setConversionSources] = useState<IConversionSource[]>([])
  const [subsidiaries, setSubsidiaries] = useState<ISubsidiary[]>([])
  const [selectedSubsidiaryId, setSelectedSubsidiaryId] = useState<string | undefined>()
  const [selectedEmissionFactorSourceId, setSelectedEmissionFactorSourceId] = useState<string | undefined>()
  const [activeCategoryId, setActiveCategoryId] = useState<string>('')
  const [backgroundColor, setBackgroundColor] = useState<string>('common.white')
  const [isFormSubmitting, setIsFormSubmitting] = useState(false)

  const { measurementCalcs, primaryGroups, additionalGroups, alternativeGroups, scope2MarketLocationPair } = useMemo(
    () => partitionCalculationGroups(calculations),
    [calculations]
  )

  const primaryReportingKg = primaryGroups.reduce((sum, g) => sum + sumGroupKg(g), 0)

  const totalGroupCount = primaryGroups.length + additionalGroups.length + alternativeGroups.length
  const isLastRecord = totalGroupCount === 1

  const closeAddActivityView = () => {
    setShowAddActivityView(false)
    setSelectedGroupId('')
    setGroup(undefined)
    setActiveCategoryId('')
    setBackgroundColor('common.white')
    setConversionSources([])
    setSelectedEmissionFactorSourceId(undefined)
  }

  const closeAddGoodsCatalogueView = () => {
    setShowAddGoodsCatalogueView(false)
    setSelectedGroupId('')
    setGroup(undefined)
    setActiveCategoryId('')
    setBackgroundColor('common.white')
    setConversionSources([])
    setSelectedEmissionFactorSourceId(undefined)
  }

  useEffect(() => {
    if (!showAddActivityView && !showAddGoodsCatalogueView && !showAddDirectView) return
    groupService.getGroups().then(setGroups).catch(console.error)
  }, [showAddActivityView, showAddGoodsCatalogueView, showAddDirectView])

  // The Direct (pre-calculated) view needs the full category list (unbound by the
  // crosswalk) and a resolved subsidiary for EmissionsInputDirect. It does not use the
  // group/category drill-down that the activity and product views share.
  useEffect(() => {
    if (!showAddDirectView) return
    categoryService.getAllCategories().then(setCategories).catch(console.error)
    subsidiaryService
      .getAll()
      .then((response) => {
        setSubsidiaries(response)
        const preferred = getPreferredSubsidiary(response, userDetails)
        setSelectedSubsidiaryId(preferred?.id ?? emission.subsidiaryId ?? undefined)
      })
      .catch(console.error)
  }, [showAddDirectView])

  useEffect(() => {
    if ((!showAddActivityView && !showAddGoodsCatalogueView) || !selectedGroupId) return
    const reportPeriodId = emission.reportPeriodId ?? reportPeriodContext.lastSelectedReportId
    if (!reportPeriodId) return
    setGroup(undefined)
    setActiveCategoryId('')
    setBackgroundColor('common.white')
    groupService
      .getGroupWithActionsForReportPeriodWithPagination(selectedGroupId, reportPeriodId, 0, 10)
      .then(setGroup)
      .catch(console.error)

    conversionSourceService.getAll(true, 'conversion').then(setConversionSources).catch(console.error)

    subsidiaryService
      .getAll()
      .then((response) => {
        setSubsidiaries(response)
        const preferred = getPreferredSubsidiary(response, userDetails)
        if (preferred) {
          setSelectedSubsidiaryId(preferred.id)
          setSelectedEmissionFactorSourceId(preferred.defaultConversionSourceId)
        }
      })
      .catch(console.error)
  }, [showAddActivityView, showAddGoodsCatalogueView, selectedGroupId, emission.reportPeriodId])

  const showCategoryFormHandler = (categoryId: string) => {
    // An empty id closes the form and returns to the category card grid (Cancel).
    if (!categoryId) {
      setActiveCategoryId('')
      return
    }
    conversionSourceService
      .getConversionSourceForCategory(categoryId, 'conversion', group?.scope ?? '', true)
      .then((response) => {
        setConversionSources(response)
        setActiveCategoryId(categoryId)
        setBackgroundColor('secondary.light')
        const selectedSubsidiary = subsidiaries.find((s) => s.id === selectedSubsidiaryId)
        const defaultId = selectedSubsidiary?.defaultConversionSourceId
        const found = response.find((cs) => cs.id === defaultId)
        setSelectedEmissionFactorSourceId(found ? found.id : undefined)
      })
      .catch(console.error)
  }

  const emissionFactorSourceHandler = (value: IConversionSource) => {
    setSelectedEmissionFactorSourceId(value.id)
  }

  // The label of the family that a replace would demote, for the snackbar. One primary
  // family gives its method name; several fall back to a neutral phrase.
  const supersededMethodLabel = (): string => {
    if (primaryGroups.length !== 1) return 'previous'
    const group = primaryGroups[0]
    const primaryCalc = group.calculations.find((c) => !coerceBool(c.isWtt)) ?? group.calculations[0]
    return formatMethodLabel(primaryCalc).toLowerCase()
  }

  const savedMessageForReplace = (): string =>
    primaryGroups.length === 1
      ? `Saved. The ${supersededMethodLabel()} calculation is no longer used in reports.`
      : 'Saved. The previous calculations are no longer used in reports.'

  // Ask replace-or-count-both before an add that would sit on top of an existing selection.
  // Resolves immediately with no dialog when there is nothing selected to replace. Rejects
  // if the user dismisses the choice, so the calling form can abort its save cleanly.
  const resolveSupersede = (): Promise<ISupersedeDecision> =>
    new Promise((resolve, reject) => {
      if (primaryGroups.length === 0) {
        resolve({ supersede: false, savedMessage: 'Saved successfully' })
        return
      }
      setSupersedeRequest({ resolve, reject })
    })

  const chooseSupersedeReplace = () => {
    supersedeRequest?.resolve({ supersede: true, savedMessage: savedMessageForReplace() })
    setSupersedeRequest(null)
  }

  const chooseSupersedeBoth = () => {
    supersedeRequest?.resolve({ supersede: false, savedMessage: 'Saved successfully' })
    setSupersedeRequest(null)
  }

  const cancelSupersedeChoice = () => {
    supersedeRequest?.reject()
    setSupersedeRequest(null)
  }

  const handleActivityTransactionSubmit = async (form: any) => {
    if (isReportPeriodLocked) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Not Allowed',
        message: 'The selected report period is locked and cannot be modified',
        buttonCopy: 'Ok',
        hideCancelButton: true,
      })
      return
    }
    if (isFormSubmitting) return
    let decision: ISupersedeDecision
    try {
      decision = await resolveSupersede()
    } catch {
      return
    }
    setIsFormSubmitting(true)
    emissionCalculationService
      .createEmissionCalculation(emission.id, form, decision.supersede)
      .then(() => {
        onAddSuccess()
        setIsFormSubmitting(false)
        userNotificationsContext.openSnackbar({ open: true, message: decision.savedMessage })
        closeAddActivityView()
      })
      .catch((err) => {
        setIsFormSubmitting(false)
        console.error(err)
        userNotificationsContext.openSnackbar({
          open: true,
          message: err.response?.data?.code === 1 ? 'Report period is locked' : 'There was an error, please try again',
          backgroundColor: 'text.primary',
        })
      })
  }

  const onActivitySave = () => {
    userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
    onAddSuccess()
    closeAddActivityView()
  }

  // The Direct input shows its own supersede-aware snackbar on save, so this only refreshes
  // and closes the view rather than adding a second toast.
  const onActivitySaveDirect = () => {
    onAddSuccess()
    closeAddDirectView()
  }

  // The goods input likewise shows its own supersede-aware snackbar, so this only refreshes
  // and closes the goods catalogue view.
  const onGoodsSave = () => {
    onAddSuccess()
    closeAddGoodsCatalogueView()
  }

  const onChangeSelectedStatus = (emissionCalculationIds: string[]) => {
    if (!canMutateCalculations) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Not Allowed',
        message: canManageCalculations
          ? 'The selected report period is locked and cannot be modified'
          : 'You do not have permission to modify emission calculations',
        buttonCopy: 'Ok',
        hideCancelButton: true,
      })
      return
    }
    emissionCalculationService
      .changeSelectedStatus(emissionCalculationIds)
      .then(() => {
        onAddSuccess()
        userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
      })
      .catch((err) => {
        console.error(err)
        userNotificationsContext.openSnackbar({
          open: true,
          message: err.response?.data?.code === 1 ? 'Report period is locked' : 'There was an error, please try again',
          backgroundColor: 'text.primary',
        })
      })
  }

  const handleSwitchScope2Totals = (target: 'market' | 'location') => {
    if (!canMutateCalculations) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Not Allowed',
        message: canManageCalculations
          ? 'The selected report period is locked and cannot be modified'
          : 'You do not have permission to modify emission calculations',
        buttonCopy: 'Ok',
        hideCancelButton: true,
      })
      return
    }
    const marketCalcs = calculations.filter(
      (c) => c.scope === '2' && c.type === 'ACTIVITY' && !coerceBool(c.isWtt) && c.isMarketBased,
    )
    const locationCalcs = calculations.filter(
      (c) => c.scope === '2' && c.type === 'ACTIVITY' && !coerceBool(c.isWtt) && !c.isMarketBased,
    )
    if (marketCalcs.length === 0 || locationCalcs.length === 0) return

    const marketSelected = marketCalcs.some((c) => coerceBool(c.isSelected))
    const wantMarket = target === 'market'
    if (wantMarket === marketSelected) return

    const currentlySelectedGroup = marketSelected ? marketCalcs : locationCalcs
    const currentlyUnselectedGroup = marketSelected ? locationCalcs : marketCalcs

    emissionCalculationService
      .changeSelectedStatus(currentlySelectedGroup.map((c) => c.id))
      .then(() => emissionCalculationService.changeSelectedStatus(currentlyUnselectedGroup.map((c) => c.id)))
      .then(() => {
        onAddSuccess()
        userNotificationsContext.openSnackbar({ open: true, message: 'Saved successfully' })
      })
      .catch((err) => {
        console.error(err)
        userNotificationsContext.openSnackbar({
          open: true,
          message: err.response?.data?.code === 1 ? 'Report period is locked' : 'There was an error, please try again',
          backgroundColor: 'text.primary',
        })
      })
  }

  const closeAddCalculationMenu = () => {
    setAddCalculationMenuAnchor(null)
  }

  const startSpendFlow = () => {
    closeAddCalculationMenu()
    setOpenAddManualDialog(true)
  }

  const startProductFlow = () => {
    closeAddCalculationMenu()
    setShowAddActivityView(false)
    setShowAddGoodsCatalogueView(true)
    setSelectedGroupId('')
  }

  const startActivityFlow = () => {
    closeAddCalculationMenu()
    setShowAddGoodsCatalogueView(false)
    setShowAddActivityView(true)
    setSelectedGroupId('')
  }

  const startDirectFlow = () => {
    closeAddCalculationMenu()
    setShowAddActivityView(false)
    setShowAddGoodsCatalogueView(false)
    // Default the group and category to the current primary so an invoice already sitting
    // under, say, Purchased goods and services keeps them and the user only types the figure.
    // Both stay editable: a DIRECT row's category is a reporting label unbound by the crosswalk.
    const primaryCalc = primaryGroups[0]?.calculations.find((c) => !coerceBool(c.isWtt)) ?? primaryGroups[0]?.calculations[0]
    setDirectGroupId(primaryCalc?.groupId ?? '')
    setDirectCategoryId(primaryCalc?.categoryId ?? '')
    setShowAddDirectView(true)
  }

  const closeAddDirectView = () => {
    setShowAddDirectView(false)
    setDirectGroupId('')
    setDirectCategoryId('')
  }

  const goodsOnlyGroups = groups.filter((g) => GOODS_GROUP_IDS.includes(g.id as (typeof GOODS_GROUP_IDS)[number]))

  const buildEditCalculationIds = (groupCalculations: IEmissionCalculation[], otherBasisCalculation?: IEmissionCalculation) => {
    const ids = new Set(groupCalculations.map((calculation) => calculation.id))
    const firstCalculation = groupCalculations[0]
    const calendarYears = new Set(groupCalculations.map((calculation) => calculation.calendarYear))
    const scopes = new Set(groupCalculations.map((calculation) => calculation.scope))

    if (otherBasisCalculation) {
      ids.add(otherBasisCalculation.id)
    }

    if (firstCalculation?.type === 'SPEND') {
      calculations
        .filter((calculation) =>
          calculation.type === 'MEASUREMENT' &&
          calendarYears.has(calculation.calendarYear) &&
          scopes.has(calculation.scope),
        )
        .forEach((calculation) => ids.add(calculation.id))
    }

    if (firstCalculation?.type === 'ACTIVITY' && !coerceBool(firstCalculation.isWtt)) {
      calculations
        .filter((calculation) =>
          calendarYears.has(calculation.calendarYear) &&
          (
            (calculation.type === 'MEASUREMENT' && scopes.has(calculation.scope)) ||
            (calculation.type === 'ACTIVITY' && coerceBool(calculation.isWtt))
          ),
        )
        .forEach((calculation) => ids.add(calculation.id))
    }

    return [...ids]
  }

  const renderCalculationCard = (g: CalculationGroup, mode: 'primary' | 'additional' | 'alternative') => {
    const firstCalc = g.calculations[0]
    const scope2DualPrimary =
      scope2MarketLocationPair &&
      mode === 'primary' &&
      firstCalc.scope === '2' &&
      firstCalc.type === 'ACTIVITY' &&
      !coerceBool(firstCalc.isWtt)
    const otherBasisCalc = scope2DualPrimary ? getScope2OtherBasisCalculation(calculations, firstCalc) : undefined
    const calculationIds = g.calculations.map((c) => c.id)
    const matchingMeasurementIds = mode === 'primary'
      ? measurementCalcs.filter((m) => m.scope === g.scope).map((m) => m.id)
      : []
    const kg = sumGroupKg(g)
    const methodName = formatMethodLabel(firstCalc)

    const comparison = mode === 'alternative' ? formatPercentVsPrimary(kg, primaryReportingKg) : null
    const isPrimarySection = mode === 'primary'
    const isAdditionalSection = mode === 'additional'
    // A WTT row always renders in the Additional section, but whether it counts
    // follows its own isSelected: when the organisation excludes WTT, the rows
    // exist deselected and must not claim to be included in reports.
    const additionalIncluded = isAdditionalSection && g.calculations.every((c) => coerceBool(c.isSelected))

    const paperAccent =
      mode === 'primary'
        ? {
          bgcolor: '#f4f8ea',
          border: '1px solid',
          borderColor: 'primary.light',
        }
        : mode === 'additional'
          ? {
            bgcolor: 'common.white',
            border: '1px solid',
            borderColor: 'divider',
          }
          : {
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
          }

    const kgFormatted = formatKgCo2e(kg)
    const emissionsLabel = kgFormatted
    const periodLabel = formatCalculationPeriodLabel(g.calculations, emission)

    const calcQuality = formatCalculationQualityLabel(firstCalc)
    let descriptionLine = ''
    if (isPrimarySection) {
      descriptionLine = [methodName, `Scope ${firstCalc.scope}`, calcQuality].join(' · ')
    } else if (isAdditionalSection) {
      const upstreamLabel = isDistributionLoss(firstCalc)
        ? 'Transmission & distribution losses'
        : 'Upstream emissions (WTT)'
      descriptionLine = [upstreamLabel, `Scope ${firstCalc.scope}`, calcQuality].join(' · ')
    } else {
      const parts = [methodName, `Scope ${firstCalc.scope}`, calcQuality]
      if (comparison) parts.push(comparison)
      descriptionLine = parts.join(' · ')
    }

    const activityReference = formatActivityReference(firstCalc)

    const contributionLine =
      isPrimarySection ? formatContributionToSelectedLabel(kg, primaryReportingKg) : null

    const cardPadding = mode === 'primary' ? { px: 2, py: 1.5 } : { px: 1.75, py: 1.25 }

    const viewCalculationIds = [...calculationIds, ...matchingMeasurementIds]
    const editCalculationIds = buildEditCalculationIds(g.calculations, otherBasisCalc)

    return (
      <Paper
        key={g.key}
        variant="outlined"
        onClick={() => onView(viewCalculationIds)}
        sx={{
          boxShadow: 'none',
          ...cardPadding,
          ...paperAccent,
          cursor: 'pointer',
        }}
      >
        <Stack
          direction="row"
          flexWrap="wrap"
          alignItems="center"
          justifyContent="flex-start"
          columnGap={2}
          rowGap={1}
          sx={{ width: '100%' }}
        >
          <Box
            sx={{
              flex: { xs: '1 1 100%', sm: '0 0 220px' },
              width: { sm: 220 },
              maxWidth: { sm: 220 },
              flexShrink: 0,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: 800,
                color: 'text.primary',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {emissionsLabel}
            </Typography>
            {periodLabel && (
              <Typography
                variant="caption"
                sx={{ color: 'text.secondary', display: 'block', lineHeight: 1.35, mt: 0.25 }}
              >
                {periodLabel}
              </Typography>
            )}
          </Box>

          <Stack
            sx={{
              flex: { xs: '1 1 100%', sm: '1 1 0' },
              minWidth: { xs: 0, sm: 200 },
              flexBasis: { sm: 0 },
              gap: 0.25,
            }}
          >
            <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 400 }}>
              {descriptionLine}
            </Typography>
            {coerceBool(firstCalc.upstreamFactorMissing) && (
              <TooltipHover
                title="No upstream factor published"
                text={'This activity would normally carry upstream (well-to-tank) emissions, but the '
                  + 'factor source publishes no matching factor for it in the unit you have used. '
                  + 'Nothing is missing from the figure shown; what is missing is the upstream '
                  + 'component that would sit alongside it in Scope 3 Category 3. Recording the same '
                  + 'activity in a different unit, where the source does publish one, would include it.'}
                backgroundColor="white"
                placement="right"
              >
                <Typography
                  variant="caption"
                  sx={{ color: 'text.secondary', display: 'inline-flex', alignItems: 'center', gap: 0.5, cursor: 'help' }}
                >
                  <InfoOutlined sx={{ fontSize: 14 }} />
                  No upstream factor published for this activity
                </Typography>
              </TooltipHover>
            )}
            {activityReference && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  lineHeight: 1.35,
                  letterSpacing: 0.1,
                }}
              >
                {activityReference}
              </Typography>
            )}
            {contributionLine && (
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                {contributionLine}
              </Typography>
            )}
          </Stack>

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{
              flex: '0 1 auto',
              maxWidth: { xs: '100%', sm: 280 },
            }}
          >
            {(isPrimarySection || additionalIncluded) && (
              <CheckCircleOutline sx={{ fontSize: 16, flexShrink: 0, color: 'text.secondary', opacity: 0.85 }} />
            )}
            <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 400 }}>
              {isPrimarySection || additionalIncluded
                ? formatIncludedInReportLabel(emission)
                : 'Not used in reporting'}
            </Typography>
          </Stack>

          <Box
            sx={{ flex: '0 0 auto', ml: { xs: 0, sm: 'auto' } }}
            onClick={(e) => e.stopPropagation()}
          >
            <TableActionButtons
              id={firstCalc.id}
              preferViewThenAdditionalThenDelete={true}
              includeView={true}
              includeEdit={canMutateCalculations && !(firstCalc.type === 'ACTIVITY' && coerceBool(firstCalc.isWtt))}
              includeDelete={!!onDelete && canMutateCalculations}
              includeOther={!!onDelete && canMutateCalculations && !isAdditionalSection && !scope2DualPrimary}
              otherButtonIcon={
                isPrimarySection ? IconName.RemoveCircleOutlineOutlined : IconName.CheckCircleOutlined
              }
              otherButtonText={isPrimarySection ? 'Exclude from reporting' : 'Include in reporting'}
              onOther={(_id: string, ...args: unknown[]) => onChangeSelectedStatus((args[0] as string[]) ?? [])}
              additionalArgs={[calculationIds]}
              onView={() => onView(viewCalculationIds)}
              onEdit={() => {
                setSelectedEditCalculation(firstCalc)
                setSelectedEditCalculationIds(editCalculationIds)
                setOpenEditCalculationDialog(true)
              }}
              onDelete={() => canMutateCalculations && onDelete?.(calculationIds, isLastRecord)}
              disableButton={false}
              additionalMenuItems={
                scope2DualPrimary && canMutateCalculations
                  ? [
                      {
                        label: firstCalc.isMarketBased
                          ? 'Use location-based for reports'
                          : 'Use market-based for reports',
                        onClick: () =>
                          handleSwitchScope2Totals(firstCalc.isMarketBased ? 'location' : 'market'),
                        icon: <SwapHoriz sx={{ cursor: 'pointer', mr: 1 }} />,
                      },
                    ]
                  : []
              }
            />
          </Box>
        </Stack>
        {otherBasisCalc && (
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              mt: 0.75,
              color: 'text.secondary',
              fontSize: '0.7rem',
              lineHeight: 1.35,
            }}
          >
            {otherBasisCalc.isMarketBased ? 'Market-based' : 'Location-based'} for reporting:{' '}
            {formatKgCo2e(otherBasisCalc.emissionAmount ?? 0)}
          </Typography>
        )}
      </Paper>
    )
  }

  // The replace-or-count-both dialog must render in every add view, not only the main list.
  // The activity, product and direct views early-return their own JSX, so without this the
  // dialog was unreachable from them: resolveSupersede set its state but nothing rendered it,
  // the save promise never resolved, and no request reached the backend.
  const supersedeDialog = (
    <Dialog open={supersedeRequest !== null} onClose={cancelSupersedeChoice} maxWidth="xs" fullWidth>
      <DialogTitle>This record already has a calculation in reports</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          You can use the new calculation instead, which keeps the current one on the record as an
          alternative but stops it counting, or count both toward this record's total.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Button variant="outlined" onClick={chooseSupersedeBoth}>
          Count both calculations
        </Button>
        <Button variant="contained" onClick={chooseSupersedeReplace}>
          Use this calculation instead
        </Button>
      </DialogActions>
    </Dialog>
  )

  if (showAddGoodsCatalogueView) {
    return (
      <>
        <Box sx={{ py: 2, maxWidth: 400 }}>
          <FormControl fullWidth>
            <InputLabel id="goods-group-select-label">Group</InputLabel>
            <Select
              labelId="goods-group-select-label"
              id="goods-group-select"
              value={selectedGroupId}
              label="Group"
              onChange={(e) => setSelectedGroupId(e.target.value)}
            >
              {sortGroupsByGhgCategory(goodsOnlyGroups).map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {formatGroupLabel(g)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {group && conversionSources.length > 0 && subsidiaries.length > 0 && selectedSubsidiaryId ? (
          <EmissionsGoodCatalogue
            group={group}
            activeCategoryId={activeCategoryId}
            backgroundColor={backgroundColor}
            onSave={onGoodsSave}
            resolveSupersede={resolveSupersede}
            isFormSubmitting={isFormSubmitting}
            handleTransactionSubmit={() => { }}
            showCategoryFormHandler={showCategoryFormHandler}
            subsidiaries={subsidiaries}
            conversionSources={conversionSources}
            selectedEmissionFactorSourceId={selectedEmissionFactorSourceId}
            emissionFactorSourceHandler={emissionFactorSourceHandler}
            setViewEmissions={() => { }}
            setActiveCategoryId={setActiveCategoryId}
            setBackgroundColor={setBackgroundColor}
            selectedSubsidiaryId={selectedSubsidiaryId}
            setSelectedSubsidiaryId={setSelectedSubsidiaryId}
            emission={emission}
          />
        ) : null}
        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={closeAddGoodsCatalogueView}>
            Cancel
          </Button>
        </Box>
        {supersedeDialog}
      </>
    )
  }

  if (showAddActivityView) {
    return (
      <>
        <Box sx={{ py: 2, maxWidth: 400 }}>
          <FormControl fullWidth>
            <InputLabel id="activity-group-select-label">Group</InputLabel>
            <Select
              labelId="activity-group-select-label"
              id="activity-group-select"
              value={selectedGroupId}
              label="Group"
              onChange={(e) => setSelectedGroupId(e.target.value)}
            >
              {sortGroupsByGhgCategory(groups).map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {formatGroupLabel(g)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        {group && conversionSources.length > 0 && subsidiaries.length > 0 && selectedSubsidiaryId ? (
          <EmissionsAddManual
            group={group}
            activeCategoryId={activeCategoryId}
            backgroundColor={backgroundColor}
            onSave={onActivitySave}
            isFormSubmitting={isFormSubmitting}
            handleTransactionSubmit={handleActivityTransactionSubmit}
            showCategoryFormHandler={showCategoryFormHandler}
            selectedEmissionFactorSourceId={selectedEmissionFactorSourceId}
            emissionFactorSourceHandler={emissionFactorSourceHandler}
            conversionSources={conversionSources}
            subsidiaries={subsidiaries}
            selectedSubsidiaryId={selectedSubsidiaryId}
            setSelectedSubsidiaryId={setSelectedSubsidiaryId}
            emission={emission}
          />
        ) : null}
        <Box sx={{ mt: 3 }}>
          <Button variant="outlined" onClick={closeAddActivityView}>
            Cancel
          </Button>
        </Box>
        {supersedeDialog}
      </>
    )
  }

  if (showAddDirectView) {
    return (
      <>
        <Box sx={{ py: 2, maxWidth: 400 }}>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="direct-group-select-label">Group</InputLabel>
            <Select
              labelId="direct-group-select-label"
              id="direct-group-select"
              value={directGroupId}
              label="Group"
              onChange={(e) => setDirectGroupId(e.target.value)}
            >
              {sortGroupsByGhgCategory(groups).map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {formatGroupLabel(g)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="direct-category-select-label">Category</InputLabel>
            <Select
              labelId="direct-category-select-label"
              id="direct-category-select"
              value={directCategoryId}
              label="Category"
              onChange={(e) => setDirectCategoryId(e.target.value)}
            >
              {[...categories]
                .sort((a, b) => getCategoryDisplayName(a).localeCompare(getCategoryDisplayName(b)))
                .map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {getCategoryDisplayName(c)}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
        {directGroupId && directCategoryId && subsidiaries.length > 0 && selectedSubsidiaryId ? (
          <EmissionsInputDirect
            emission={emission}
            groupId={directGroupId}
            categoryId={directCategoryId}
            subsidiaries={subsidiaries}
            selectedSubsidiaryId={selectedSubsidiaryId}
            onSave={onActivitySaveDirect}
            resolveSupersede={resolveSupersede}
            cancelHandler={closeAddDirectView}
          />
        ) : (
          <Box sx={{ mt: 3 }}>
            <Button variant="outlined" onClick={closeAddDirectView}>
              Cancel
            </Button>
          </Box>
        )}
        {supersedeDialog}
      </>
    )
  }

  return (
    <>
      <Stack spacing={0} sx={{ mt: 1 }}>
        <Stack spacing={2} sx={{ pb: 5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mt: 0, lineHeight: 1.25 }}>
              Selected calculations
            </Typography>
            {canMutateCalculations && (
              <>
                <Button
                  id="add-calculation-button"
                  variant="text"
                  color="primary"
                  startIcon={<Add />}
                  onClick={(e) => setAddCalculationMenuAnchor(e.currentTarget)}
                  aria-controls={addCalculationMenuOpen ? 'add-calculation-menu' : undefined}
                  aria-expanded={addCalculationMenuOpen ? 'true' : undefined}
                  aria-haspopup="true"
                >
                  Add calculation
                </Button>
                <Menu
                  id="add-calculation-menu"
                  anchorEl={addCalculationMenuAnchor}
                  open={addCalculationMenuOpen}
                  onClose={closeAddCalculationMenu}
                  anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                  transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  MenuListProps={{ 'aria-labelledby': 'add-calculation-button' }}
                  PaperProps={{
                    elevation: 8,
                    sx: {
                      borderRadius: 2,
                      mt: 0.5,
                      minWidth: 280,
                      maxWidth: 360,
                    },
                  }}
                >
                  <MenuItem onClick={startSpendFlow} sx={addCalculationMenuItemSx}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.35 }}>
                      <IconPicker icon={IconName.CurrencyPound} color="text.secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Spend-based"
                      secondary="Spend or monetary activity data"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', sx: { mt: 0.25 } }}
                    />
                  </MenuItem>
                  <MenuItem onClick={startActivityFlow} sx={addCalculationMenuItemSx}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.35 }}>
                      <IconPicker icon={IconName.AssignmentOutlined} color="text.secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Activity-based"
                      secondary="Energy, fuel, or activity quantities"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', sx: { mt: 0.25 } }}
                    />
                  </MenuItem>
                  <MenuItem onClick={startProductFlow} sx={addCalculationMenuItemSx}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.35 }}>
                      <IconPicker icon={IconName.Inventory2Outlined} color="text.secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Product-based"
                      secondary="Goods or product catalogue"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', sx: { mt: 0.25 } }}
                    />
                  </MenuItem>
                  <MenuItem onClick={startDirectFlow} sx={addCalculationMenuItemSx}>
                    <ListItemIcon sx={{ minWidth: 40, mt: 0.35 }}>
                      <IconPicker icon={IconName.CheckCircleOutlined} color="text.secondary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Direct (pre-calculated)"
                      secondary="A kg CO2e figure you already have, for example from a supplier report"
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption', sx: { mt: 0.25 } }}
                    />
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>

          {primaryGroups.length === 0 && (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No calculation is selected for reporting yet. Choose a method below or add a new calculation.
            </Typography>
          )}

          {primaryGroups.map((g) => (
            <Box key={g.key}>{renderCalculationCard(g, 'primary')}</Box>
          ))}
        </Stack>

        {additionalGroups.length > 0 ? (
          <>
            <Divider sx={{ borderColor: 'divider', opacity: 0.9 }} />
            <Stack spacing={2} sx={{ pt: 2.5, pb: 5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                <Stack direction="row" alignItems="center" gap={0.75}>
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mt: 0, lineHeight: 1.25 }}>
                    Additional emissions
                  </Typography>
                  <TooltipHover
                    title="Additional emissions"
                    text={ADDITIONAL_EMISSIONS_TOOLTIP}
                    backgroundColor="white"
                    placement="right"
                  >
                    <InfoOutlined sx={{ color: 'text.secondary', fontSize: 18, cursor: 'help' }} />
                  </TooltipHover>
                </Stack>
              </Stack>
              <Stack spacing={1}>
                {additionalGroups.map((g) => (
                  <Box key={g.key}>{renderCalculationCard(g, 'additional')}</Box>
                ))}
              </Stack>
            </Stack>
          </>
        ) : null}

        <Divider sx={{ borderColor: 'divider', opacity: 0.9 }} />

        <Stack spacing={2} sx={{ pt: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mt: 0, lineHeight: 1.25 }}>
              Alternative calculations
            </Typography>
          </Stack>
          {alternativeGroups.length === 0 ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              No alternative calculations yet
            </Typography>
          ) : (
            <Stack spacing={1}>
              {alternativeGroups.map((g) => (
                <Box key={g.key}>{renderCalculationCard(g, 'alternative')}</Box>
              ))}
            </Stack>
          )}
        </Stack>

      </Stack>

      {openAddManualDialog && (
        <AddManualSpendBasedDialog
          open={openAddManualDialog}
          emission={emission}
          resolveSupersede={resolveSupersede}
          closeHandler={() => setOpenAddManualDialog(false)}
          successHandler={() => {
            onAddSuccess()
            setOpenAddManualDialog(false)
          }}
        />
      )}

      {supersedeDialog}
      {openEditCalculationDialog && selectedEditCalculation && (
        <EditEmissionCalculationDialog
          open={openEditCalculationDialog}
          calculation={selectedEditCalculation}
          calculationIds={selectedEditCalculationIds}
          calculations={calculations}
          emission={emission}
          closeHandler={() => {
            setOpenEditCalculationDialog(false)
            setSelectedEditCalculation(null)
            setSelectedEditCalculationIds([])
          }}
          successHandler={() => {
            onAddSuccess()
            setOpenEditCalculationDialog(false)
            setSelectedEditCalculation(null)
            setSelectedEditCalculationIds([])
          }}
        />
      )}
    </>
  )
}
